import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import { useCreateTemplate, useValidateTemplate } from '../../hooks/use-admin-templates.js';

const styles = {
  container: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
    overflow: 'hidden',
  },
  header: {
    padding: '1rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: tokens.colors.textDark,
    margin: 0,
  },
  body: { padding: '1.5rem', display: 'flex', flexDirection: 'column' as const, gap: '1rem' },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: tokens.colors.textDark,
    marginBottom: '0.375rem',
  },
  input: {
    width: '100%',
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    outline: 'none',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    minHeight: '280px',
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: '#e2e8f0',
    padding: '0.75rem',
    fontSize: '0.8125rem',
    fontFamily: 'monospace',
    outline: 'none',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    paddingTop: '0.5rem',
  },
  btnPrimary: {
    backgroundColor: tokens.colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnSecondary: {
    backgroundColor: 'transparent',
    color: tokens.colors.textDark,
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.button,
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
  issueList: {
    backgroundColor: '#2d1b1b',
    border: '1px solid #7f1d1d',
    borderRadius: tokens.radius.card,
    padding: '0.75rem 1rem',
    fontSize: '0.8125rem',
    color: '#fca5a5',
  },
  successMsg: {
    backgroundColor: '#14532d',
    border: '1px solid #166534',
    borderRadius: tokens.radius.card,
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    color: '#bbf7d0',
  },
};

interface TemplateCreateFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function TemplateCreateForm({ onSuccess, onCancel }: TemplateCreateFormProps) {
  const [jsonText, setJsonText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<{
    ok: boolean;
    issues: unknown[];
  } | null>(null);

  const validate = useValidateTemplate();
  const create = useCreateTemplate();

  function handleValidate() {
    setParseError(null);
    setValidationResult(null);
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(jsonText) as Record<string, unknown>;
    } catch {
      setParseError('Invalid JSON. Please check the syntax.');
      return;
    }
    validate.mutate(parsed, {
      onSuccess: (res) => setValidationResult(res),
    });
  }

  function handlePublish() {
    setParseError(null);
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(jsonText) as Record<string, unknown>;
    } catch {
      setParseError('Invalid JSON. Please check the syntax.');
      return;
    }
    create.mutate(
      {
        code: String(parsed.code ?? ''),
        kind: (parsed.kind as 'report' | 'dashboard') ?? 'report',
        category: String(parsed.category ?? ''),
        definition: parsed,
        ...(parsed.changelog !== undefined && { changelog: String(parsed.changelog) }),
      },
      { ...(onSuccess && { onSuccess }) },
    );
  }

  const canPublish = validationResult?.ok === true && !create.isPending;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>New Template</h2>
      </div>
      <div style={styles.body}>
        <div>
          <label style={styles.label} htmlFor="template-json">
            Template JSON
          </label>
          <textarea
            id="template-json"
            style={styles.textarea}
            value={jsonText}
            onChange={(e) => {
              setJsonText(e.target.value);
              setValidationResult(null);
              setParseError(null);
            }}
            placeholder={
              '{\n  "code": "my_report",\n  "kind": "report",\n  "category": "Sales",\n  ...\n}'
            }
            aria-label="Template JSON"
          />
        </div>

        {parseError && (
          <div style={styles.issueList} role="alert">
            {parseError}
          </div>
        )}

        {validationResult && !validationResult.ok && (
          <div style={styles.issueList} role="alert">
            <strong>Validation issues:</strong>
            <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
              {validationResult.issues.map((issue) => {
                const key = JSON.stringify(issue);
                return <li key={key}>{key}</li>;
              })}
            </ul>
          </div>
        )}

        {validationResult?.ok && (
          <output style={{ ...styles.successMsg, display: 'block' }}>
            Template JSON is valid. Ready to publish.
          </output>
        )}

        {create.isSuccess && (
          <output style={{ ...styles.successMsg, display: 'block' }}>
            Template published successfully.
          </output>
        )}

        {create.isError && (
          <div style={styles.issueList} role="alert">
            Error publishing template.
          </div>
        )}

        <div style={styles.actions}>
          <button
            type="button"
            style={styles.btnSecondary}
            onClick={handleValidate}
            disabled={validate.isPending || jsonText.trim().length === 0}
            aria-label="Validate template JSON"
          >
            {validate.isPending ? 'Validating…' : 'Validate'}
          </button>
          <button
            type="button"
            style={{ ...styles.btnPrimary, opacity: canPublish ? 1 : 0.5 }}
            onClick={handlePublish}
            disabled={!canPublish}
            aria-label="Publish template"
          >
            {create.isPending ? 'Publishing…' : 'Publish'}
          </button>
          <button type="button" style={styles.btnSecondary} onClick={onCancel} aria-label="Cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
